import { useRef, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { addAlternativeAt } from '@/lib/gapTemplateEdit';
import { TABLE_MAX_COLS, TABLE_MAX_ROWS, tableModel, tableRows, type TableFillContent } from '@/lib/tableFill';
import { cn } from '@/lib/utils';

/**
 * Редактор «Дополни таблицу» (`table_fill`, решение владельца 13.10.2026; макет —
 * «Редактор таблицы» на холсте https://claude.ai/artifact/4BN762UqobUnieUrTUWErB).
 *
 * Ячейка — простой текст с пропусками в синтаксисе `gap_fill`: `[[went]]` — вся
 * ячейка, `She [[goes|is going]] to school.` — пропуск внутри предложения.
 * «Сделать пропуском» оборачивает выделенное в `[[…]]`, без выделения — всю ячейку;
 * «Вариант ответа» дописывает `|` в пропуск под курсором (lib/gapTemplateEdit.ts).
 *
 * ⚠️ ТАБЛИЦА ВСЕГДА ПРЯМОУГОЛЬНАЯ: добавление и удаление строки и столбца правят все
 * строки сразу. Пределы — TABLE_MAX_COLS × TABLE_MAX_ROWS, их же держит сторож сервера
 * (courses-service block_content_guard.go): больше — сохранение отказом.
 */
export function TableFillEditor({
  content,
  onChange,
}: {
  content: Record<string, unknown>;
  onChange: (n: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  const table = content as TableFillContent;
  const rows = tableRows(table);
  const cols = rows[0]?.length ?? 0;
  const header = content.header === true;
  const mode = content.mode === 'drag' ? 'drag' : 'input';
  const gapCount = tableModel(table).gaps.length;
  const [focus, setFocus] = useState<{ r: number; c: number } | null>(null);
  const [altMiss, setAltMiss] = useState(false);
  const refs = useRef<Record<string, HTMLTextAreaElement | null>>({});

  const setRows = (next: string[][]) => onChange({ ...content, rows: next });
  const setCell = (r: number, c: number, v: string) =>
    setRows(rows.map((row, ri) => (ri === r ? row.map((x, ci) => (ci === c ? v : x)) : row)));

  const addRow = () => setRows([...rows, Array(Math.max(cols, 1)).fill('')]);
  const addCol = () => setRows((rows.length ? rows : [[]]).map((row) => [...row, '']));
  const delRow = (r: number) => setRows(rows.filter((_, ri) => ri !== r));
  const delCol = (c: number) => setRows(rows.map((row) => row.filter((_, ci) => ci !== c)));

  // Курсор и выделение берём из поля ячейки, где стоит фокус; кнопки — по mousedown
  // без потери фокуса, как на панели RichTextEditor.
  const focused = () => (focus ? refs.current[`${focus.r}:${focus.c}`] : null);
  const makeGap = () => {
    const ta = focused();
    if (!focus || !ta) return;
    const text = rows[focus.r][focus.c];
    const a = ta.selectionStart ?? 0;
    const b = ta.selectionEnd ?? 0;
    if (a !== b) setCell(focus.r, focus.c, `${text.slice(0, a)}[[${text.slice(a, b)}]]${text.slice(b)}`);
    else if (text.trim() && !/^\s*\[\[[^\]]*\]\]\s*$/.test(text)) setCell(focus.r, focus.c, `[[${text.trim()}]]`);
  };
  const addAlternative = () => {
    const ta = focused();
    if (!focus || !ta) return;
    const next = addAlternativeAt(rows[focus.r][focus.c], ta.selectionStart ?? -1);
    setAltMiss(next === null);
    if (!next) return;
    setCell(focus.r, focus.c, next.template);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(next.caret, next.caret);
    });
  };
  const keepFocus = (e: MouseEvent) => e.preventDefault();

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-on-surface">
          <input type="checkbox" checked={header} onChange={(e) => onChange({ ...content, header: e.target.checked })} />
          {t('blockRegistry.tblHeader')}
        </label>
        <span className="flex items-center gap-2 text-sm text-on-surface-variant">
          {t('blockRegistry.gapMode')}
          <select
            value={mode}
            onChange={(e) => onChange({ ...content, mode: e.target.value })}
            className="rounded-button bg-surface-container-low p-2 text-sm text-on-surface focus:outline-none"
          >
            <option value="input">{t('blockRegistry.gapModeInput')}</option>
            <option value="drag">{t('blockRegistry.gapModeDrag')}</option>
          </select>
        </span>
        {mode === 'input' && (
          <label className="flex items-center gap-2 text-sm text-on-surface">
            <input
              type="checkbox"
              checked={content.case_sensitive === true}
              onChange={(e) => {
                const next = { ...content };
                if (e.target.checked) next.case_sensitive = true;
                else delete next.case_sensitive;
                onChange(next);
              }}
            />
            {t('blockRegistry.caseSensitive')}
          </label>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onMouseDown={keepFocus} onClick={makeGap} disabled={!focus} className="flex items-center gap-1 text-sm font-medium text-primary disabled:opacity-50">
          <span className="material-symbols-outlined text-base">add_box</span>
          {t('blockRegistry.tblMakeGap')}
        </button>
        <button type="button" onMouseDown={keepFocus} onClick={addAlternative} disabled={!focus} className="flex items-center gap-1 text-sm font-medium text-primary disabled:opacity-50">
          <span className="material-symbols-outlined text-base">add</span>
          {t('blockRegistry.addAnswerVariant')}
        </button>
        <span className="ml-auto text-xs text-on-surface-variant">{t('blockRegistry.gapCount', { count: gapCount })}</span>
      </div>
      {altMiss && <p className="text-xs text-on-surface-variant">{t('blockRegistry.addAnswerVariantHint')}</p>}

      {/* Сетка правки; на узком экране — прокрутка вбок внутри блока. */}
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-1">
          <thead>
            <tr>
              {Array.from({ length: cols }, (_, ci) => (
                <th key={ci} className="min-w-[9rem] text-right">
                  <button
                    type="button"
                    onClick={() => delCol(ci)}
                    disabled={cols <= 1}
                    title={t('blockRegistry.tblDeleteCol', { n: ci + 1 })}
                    aria-label={t('blockRegistry.tblDeleteCol', { n: ci + 1 })}
                    className="rounded-button p-1 text-on-surface-variant hover:text-error disabled:opacity-30"
                  >
                    <span className="material-symbols-outlined text-base">close</span>
                  </button>
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci} className="align-top">
                    <textarea
                      ref={(el) => { refs.current[`${ri}:${ci}`] = el; }}
                      rows={cell.length > 40 ? 3 : 1}
                      value={cell}
                      onFocus={() => setFocus({ r: ri, c: ci })}
                      onChange={(e) => setCell(ri, ci, e.target.value)}
                      placeholder={t('blockRegistry.tblCellPh')}
                      aria-label={t('blockRegistry.tblCell', { r: ri + 1, c: ci + 1 })}
                      className={cn(
                        'w-full resize-y rounded-button bg-surface-container-low p-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary',
                        header && ri === 0 && 'font-semibold',
                      )}
                    />
                  </td>
                ))}
                <td className="align-top">
                  <button
                    type="button"
                    onClick={() => delRow(ri)}
                    disabled={rows.length <= 1}
                    title={t('blockRegistry.tblDeleteRow', { n: ri + 1 })}
                    aria-label={t('blockRegistry.tblDeleteRow', { n: ri + 1 })}
                    className="rounded-button p-1 text-on-surface-variant hover:text-error disabled:opacity-30"
                  >
                    <span className="material-symbols-outlined text-base">delete</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <button type="button" onClick={addRow} disabled={rows.length >= TABLE_MAX_ROWS} className="flex items-center gap-1 text-sm font-medium text-primary disabled:opacity-50">
          <span className="material-symbols-outlined text-base">add</span>
          {t('blockRegistry.tblAddRow')}
        </button>
        <button type="button" onClick={addCol} disabled={cols >= TABLE_MAX_COLS} className="flex items-center gap-1 text-sm font-medium text-primary disabled:opacity-50">
          <span className="material-symbols-outlined text-base">add</span>
          {t('blockRegistry.tblAddCol')}
        </button>
      </div>
      <p className="text-xs text-on-surface-variant">{t('blockRegistry.tblHint')}</p>

      {mode === 'drag' && (
        <input
          type="text"
          value={((content.distractors as string[]) ?? []).join(', ')}
          onChange={(e) => onChange({ ...content, distractors: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
          placeholder={t('blockRegistry.gapDistractors')}
          className="w-full rounded-button bg-surface-container-low p-2.5 text-sm text-on-surface focus:outline-none"
        />
      )}
    </div>
  );
}
